import { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'wouter';
import { UserCog, Eye, LogOut, Shield, Clock } from 'lucide-react';
import { UnifiedButton } from '@/components/ui/UnifiedButton';
import { useAuth } from '@/hooks/useAuth';
import { cn } from '@/lib/utils';

/**
 * ImpersonationBanner - Sticky banner displayed when admin is impersonating a user
 *
 * Security features:
 * - Always visible at top of screen during impersonation
 * - Shows impersonated user details
 * - Timer showing session duration
 * - Clear "End Impersonation" action
 */
export function ImpersonationBanner() {
  const { isImpersonating, impersonatedUser, endImpersonation, impersonationStartTime } = useAuth();
  const [, setLocation] = useLocation();

  const [sessionDuration, setSessionDuration] = useState('00:00');
  const [isEnding, setIsEnding] = useState(false);

  // Update session duration every second
  useEffect(() => {
    if (!isImpersonating || !impersonationStartTime) return;

    const updateDuration = () => {
      const start = new Date(impersonationStartTime).getTime();
      const now = Date.now();
      const diffMs = now - start;

      const minutes = Math.floor(diffMs / 60000);
      const seconds = Math.floor((diffMs % 60000) / 1000);

      setSessionDuration(
        `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`,
      );
    };

    updateDuration();
    const interval = setInterval(updateDuration, 1000);

    return () => clearInterval(interval);
  }, [isImpersonating, impersonationStartTime]);

  // Emergency fallback - clears all auth state and redirects to login
  const forceEndSession = useCallback(() => {
    // Clear all storage
    localStorage.removeItem('auth_token');
    localStorage.removeItem('token');
    sessionStorage.removeItem('impersonation_state');
    sessionStorage.removeItem('original_admin_token');
    // Redirect to login
    window.location.href = '/sign-in';
  }, []);

  const handleEndImpersonation = useCallback(async () => {
    setIsEnding(true);
    try {
      const success = await endImpersonation();
      if (success) {
        // Return admin to admin dashboard instead of staying on impersonated page
        setLocation('/admin');
      } else {
        // Fallback: Force clear everything and redirect to login
        forceEndSession();
      }
    } catch {
      // Emergency fallback
      forceEndSession();
    } finally {
      setIsEnding(false);
    }
  }, [endImpersonation, forceEndSession, setLocation]);

  // Don't render if not impersonating
  if (!isImpersonating || !impersonatedUser) {
    return null;
  }

  const userName =
    [impersonatedUser.firstName, impersonatedUser.lastName].filter(Boolean).join(' ') ||
    impersonatedUser.displayName ||
    impersonatedUser.username ||
    'Unknown User';

  return (
    <div
      className={cn(
        'fixed top-0 left-0 right-0 z-[100]',
        'bg-amber-500/20 backdrop-blur-xl border-b border-amber-500/50',
        'shadow-lg shadow-amber-500/10',
      )}
    >
      <div className="container mx-auto px-4 py-2">
        <div className="flex items-center justify-between flex-wrap gap-2">
          {/* Left: Warning icon and user info */}
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 text-amber-400">
              <Shield className="w-5 h-5" />
              <Eye className="w-4 h-4" />
            </div>

            <div className="flex items-center gap-2">
              <UserCog className="w-4 h-4 text-amber-400" />
              <span className="text-amber-400 font-medium text-sm">Viewing as:</span>
              <span className="text-brite-ink font-semibold">{userName}</span>
              {impersonatedUser.email && (
                <span className="text-amber-300/70 text-sm hidden sm:inline">
                  ({impersonatedUser.email})
                </span>
              )}
            </div>
          </div>

          {/* Right: Timer and End button */}
          <div className="flex items-center gap-4">
            {/* Session timer */}
            <div className="flex items-center gap-1.5 text-amber-300/80 text-sm">
              <Clock className="w-4 h-4" />
              <span className="font-mono">{sessionDuration}</span>
            </div>

            {/* End impersonation button */}
            <UnifiedButton
              size="sm"
              variant="outline"
              onClick={handleEndImpersonation}
              loading={isEnding}
              leftIcon={<LogOut className="w-4 h-4" />}
              className={cn(
                'bg-amber-500/20 border-amber-500/50 text-amber-400',
                'hover:bg-amber-500/30 hover:border-amber-400 hover:text-amber-300',
                'transition-all duration-200',
              )}
            >
              End Impersonation
            </UnifiedButton>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ImpersonationBanner;
